import { useEffect, useMemo, useState, type FormEvent } from "react";
import { saveOrganisationBranding } from "@/lib/api";
import type { Organisation } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { brandingFromSettings, contrastColour } from "@/lib/branding";
import { supabase } from "@/lib/supabase";
import type { Permission } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Empty } from "@/components/empty-state";
import { FieldError } from "@/components/field-error";

export function Branding({
  organisation,
  can,
  onSaved,
}: {
  organisation: Organisation;
  can: (permission: Permission) => boolean;
  onSaved: (organisation: Organisation) => void;
}) {
  const current = brandingFromSettings(
    organisation.name,
    organisation.settings,
  );
  if (current.logoPath)
    current.logoUrl = supabase.storage
      .from("raqoz-brand-assets")
      .getPublicUrl(current.logoPath).data.publicUrl;
  const [primaryColor, setPrimaryColor] = useState(current.primaryColor);
  const [accentColor, setAccentColor] = useState(current.accentColor);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const previewLogo = useMemo(
    () =>
      logoFile
        ? URL.createObjectURL(logoFile)
        : removeLogo
          ? null
          : current.logoUrl,
    [logoFile, removeLogo, current.logoUrl],
  );

  useEffect(
    () => () => {
      if (previewLogo?.startsWith("blob:")) URL.revokeObjectURL(previewLogo);
    },
    [previewLogo],
  );
  if (!can("manage"))
    return (
      <>
        <PageHeader
          eyebrow="WORKSPACE IDENTITY"
          title="Brand appearance"
          description="Your role does not permit organisation branding changes."
        />
        <Empty
          title="Administrator access required"
          description="An organisation administrator or Cyclotron platform owner manages the workspace identity."
        />
      </>
    );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const updated = await saveOrganisationBranding(organisation.id, {
        primaryColor,
        accentColor,
        currentLogoPath: current.logoPath,
        logoFile,
        removeLogo,
      });
      onSaved(updated);
      setLogoFile(null);
      setRemoveLogo(false);
      setMessage(
        "Workspace identity saved. This organisation’s sign-in now uses its own logo and colour system.",
      );
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="WORKSPACE IDENTITY"
        title="Brand appearance"
        description="Give this organisation a recognisable sign-in while RAQOZ remains the secure operating layer."
      />
      <div className="grid gap-5 xl:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <form onSubmit={submit} className="grid gap-6">
            <div>
              <h2 className="font-serif text-xl">Logo and colours</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                These public-safe values shape this organisation’s sign-in and
                workspace identity.
              </p>
            </div>
            <Label>
              Organisation logo
              <Input
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  setLogoFile(event.target.files?.[0] || null);
                  setRemoveLogo(false);
                }}
                type="file"
              />
            </Label>
            <p className="-mt-4 text-xs text-slate-500">
              PNG, JPEG or WebP · maximum 2 MB
            </p>
            {current.logoPath && !removeLogo && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLogoFile(null);
                  setRemoveLogo(true);
                }}
              >
                Remove current logo
              </Button>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Label>
                Primary colour
                <div className="mt-1 flex items-center gap-3">
                  <Input
                    aria-label="Primary brand colour"
                    className="h-11 w-16 p-1"
                    type="color"
                    value={primaryColor}
                    onChange={(event) => setPrimaryColor(event.target.value)}
                  />
                  <code className="text-xs">{primaryColor}</code>
                </div>
              </Label>
              <Label>
                Accent colour
                <div className="mt-1 flex items-center gap-3">
                  <Input
                    aria-label="Accent brand colour"
                    className="h-11 w-16 p-1"
                    type="color"
                    value={accentColor}
                    onChange={(event) => setAccentColor(event.target.value)}
                  />
                  <code className="text-xs">{accentColor}</code>
                </div>
              </Label>
            </div>
            <FieldError>{error}</FieldError>
            {message && (
              <p
                className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
                role="status"
              >
                {message}
              </p>
            )}
            <Button disabled={busy} type="submit">
              {busy ? "Saving identity…" : "Save workspace identity"}
            </Button>
          </form>
        </Panel>
        <section className="overflow-hidden rounded-[2rem] shadow-[0_25px_80px_rgba(15,35,45,.16)]">
          <div
            className="flex min-h-[480px] flex-col p-10 text-white"
            style={{
              backgroundColor: primaryColor,
              color: contrastColour(primaryColor),
            }}
          >
            {previewLogo ? (
              <img
                alt={`${organisation.name} logo preview`}
                className="h-16 max-w-[240px] object-contain object-left"
                src={previewLogo}
              />
            ) : (
              <RaqozLogo inverse />
            )}
            <div className="my-auto">
              <p className="text-xs font-black tracking-[.15em] opacity-65">
                {organisation.name.toUpperCase()} WORKSPACE
              </p>
              <h2 className="mt-5 max-w-lg font-serif text-4xl leading-tight">
                Your team’s purchase journey, in your organisation’s identity.
              </h2>
              <p className="mt-5 max-w-md text-sm leading-6 opacity-70">
                Requisition to receipt, with commercial clarity and an audit
                trail by design.
              </p>
              <span
                className="mt-8 inline-flex rounded-xl px-5 py-3 text-sm font-bold"
                style={{
                  backgroundColor: accentColor,
                  color: contrastColour(accentColor),
                }}
              >
                Enter workspace
              </span>
            </div>
            <p className="text-[10px] font-bold tracking-[.14em] opacity-45">
              POWERED BY RAQOZ · A CYCLOTRON TECHNOLOGIES PLATFORM
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
